import { SMTFallbackUIConfig } from "@/constants/SMT";
import { SMTTestID, TextTestID } from "@/constants/TestID";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { Avatar, Text } from "@/ui/core";
import { BaseCard, HStack, VStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";
import { getPreferredFullName } from "@/utils/user.util";

interface PatientCardProps extends ViewWithTestID {
  externalId: string;
  onPress: () => void;
  userName: string;
}

const PatientCard = ({
  externalId: userId,
  onPress,
  testID,
  userName,
}: PatientCardProps) => {
  return (
    <BaseCard onPress={onPress} testID={testID} variant="avatarBlock">
      <HStack alignItems="center" gap={12}>
        <Avatar
          size="sm"
          testID={testID}
          userId={userId}
          userName={userName}
          userType="patient"
        />
        <Text testID={testID} variant="bodyMedium">
          {userName}
        </Text>
      </HStack>
    </BaseCard>
  );
};

interface SMTPatientSelectorModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  onDismissAnimationEnd?: () => void;
  onPatientSelected: (patientExternalId: string) => void;
  patients: LinkedPatientProfile[];
  uiConfig?: SMTPatientSelectorUIConfig;
}

const SMTPatientSelectorModal = ({
  isOpen,
  onDismiss,
  onDismissAnimationEnd,
  onPatientSelected,
  patients,
  uiConfig = SMTFallbackUIConfig.addFood.selectPatient,
}: SMTPatientSelectorModalProps) => {
  const baseTestID = joinTestIDs(
    SMTTestID.Primary,
    SMTTestID.SelectPatientModal,
  );
  return (
    <BottomSheetModal
      dismissAccessibilityLabel="Dismiss SMT Patient Selector Modal"
      isOpen={isOpen}
      onDismissAnimationEnd={onDismissAnimationEnd}
      onRequestClose={onDismiss}
      testID={baseTestID}
      title={uiConfig.title}
    >
      <VStack gap={18}>
        <Text
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          variant="body"
        >
          {uiConfig.description}
        </Text>
        <VStack alignItems="center" gap={12} justifyContent="center">
          {patients.map((patient: LinkedPatientProfile) => {
            const { chosenName, externalId, firstName, lastName } = patient;
            const userName = getPreferredFullName({
              chosenName,
              firstName,
              lastName,
            });
            return (
              <PatientCard
                externalId={externalId}
                key={externalId}
                onPress={() => onPatientSelected(externalId)}
                testID={joinTestIDs(baseTestID, externalId)}
                userName={userName}
              />
            );
          })}
        </VStack>
      </VStack>
    </BottomSheetModal>
  );
};

export type { SMTPatientSelectorModalProps };
export default SMTPatientSelectorModal;
